import { Label } from "./label"
import {
  FieldValues,
  UseFormRegisterReturn,
  UseFormReturn,
} from "react-hook-form"
import { ErrorHint } from "./errorhint"
import { cloneElement, ReactElement, ReactNode, useId } from "react"
import { ErrorMessage } from "./error-messages"
import { FlatNamespace } from "i18next"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"
import { Info } from "lucide-react"
import { cn } from "@/lib/utils"

export function FormField({
  label,
  children,
  className,
  errors,
  ...registerReturn
}: {
  label: string
  children: ReactElement<any>
  errors?: React.ComponentProps<typeof ErrorMessage>["errors"]
  className?: string
} & UseFormRegisterReturn): ReactNode

export function FormField<
  TName extends React.ComponentProps<typeof ErrorMessage>["name"],
  TFieldValues extends FieldValues,
>({
  label,
  children,
  control,
  name,
  errors,
  className,
}: {
  label: string
  children: ReactElement<any>
  control: UseFormReturn<TFieldValues>["control"]
  name: TName
  errors?: React.ComponentProps<typeof ErrorMessage>["errors"]
  className?: string
}): ReactNode

export function FormField<
  TName extends React.ComponentProps<typeof ErrorMessage>["name"],
  TFieldValues extends FieldValues,
>({
  label,
  children,
  control,
  name,
  errors,
  className,
  namespaces = ["common"],
  tooltipContent,
  ...registerReturn
}: {
  label: string
  children: ReactElement<any>
  name?: TName
  errors?: React.ComponentProps<typeof ErrorMessage>["errors"]
  control?: UseFormReturn<TFieldValues>["control"]
  namespaces?: FlatNamespace | FlatNamespace[]
  className?: string
  tooltipContent?: React.ReactNode
} & Partial<UseFormRegisterReturn>) {
  const fieldProps = control
    ? { control }
    : (registerReturn as UseFormRegisterReturn)
  const id = useId()
  const labelId = useId()

  return (
    <div className={cn("", tooltipContent && "flex w-full flex-row")}>
      <FormGroup
        htmlFor={id}
        label={label}
        labelId={labelId}
        className={cn(className, "grow space-y-2")}
      >
        {cloneElement(children, {
          ...fieldProps,
          id,
          name,
          "aria-labelledby": labelId,
        })}
        {errors && name && (
          <ErrorMessage
            namespaces={namespaces}
            name={name}
            errors={errors}
            render={ErrorHint}
          />
        )}
      </FormGroup>
      {tooltipContent ? (
        <Tooltip>
          <TooltipTrigger className="self-center pt-6" asChild>
            <div className="text-primary px-4">
              <Info />
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-64" align="center" side="left">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

export function FormGroup({
  children,
  className,
  htmlFor,
  label,
  labelId,
}: {
  children: ReactNode
  htmlFor?: string
  className?: string
  label?: string
  labelId?: string
}) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} id={labelId}>
        {label}
      </Label>
      {children}
    </div>
  )
}
